<script setup lang="ts">
import { CaseListType, CaseQuickFilterType, useOptionsStore } from "@/App/options/options.store";
import { useCaseNavBarOptions } from "@/Pages/Cases/components/CasesNavbar/composables/useCaseNavBarOptions";
import AudioNotificationForCaseUpdates from "@/Pages/Cases/components/CasesNavbar/AudioNotificationForCaseUpdates.vue";
import { useCasesStore } from "@/Pages/Cases/store";
import { CaseMode } from "@/App/types/CaseMode";

const casesStore = useCasesStore();
const optionsStore = useOptionsStore();
const caseNavBarOptions = useCaseNavBarOptions();
</script>

<template>
  <span
    class="no-width"
    aria-hidden="true"
    title="Options"
    data-bs-toggle="offcanvas"
    data-bs-target="#offcanvasDeshboardOptions"
    aria-controls="offcanvasDeshboardOptions"
  ></span>

  <div id="offcanvasDeshboardOptions" class="offcanvas offcanvas-end" tabindex="-1">
    <div class="offcanvas-body">
      <b>Options</b>
      <hr />
      <ul class="list-group mt-3">
        <li class="list-group-item">
          <span class="icon fa-sm fa-solid fa-list"></span>
          <span class="text ms-2">Case List Display</span>
          <br />
          <input
            id="default"
            :checked="optionsStore.caseListType === CaseListType.Default"
            type="radio"
            name="caseListType"
            :value="CaseListType.Default"
            @change.stop="caseNavBarOptions.onSaveCaseListType"
          />
          <label for="default">Default</label><br />
          <input
            id="compact"
            :checked="optionsStore.caseListType === CaseListType.Compact"
            type="radio"
            name="caseListType"
            :value="CaseListType.Compact"
            @change.stop="caseNavBarOptions.onSaveCaseListType"
          />
          <label for="compact">Compact</label><br />
          <input
            id="map"
            :checked="optionsStore.caseListType === CaseListType.Map"
            type="radio"
            name="caseListType"
            :value="CaseListType.Map"
            @change.stop="caseNavBarOptions.onSaveCaseListType"
          />
          <label for="map">Map View</label>
          <p class="option-description">Allows you to change the look of the Case List display.</p>
        </li>
        <li class="list-group-item">
          <span class="icon fa-sm fa-solid fa-list"></span>
          <span class="text ms-2">Show Case List Quick Filters</span>
          <br />
          <input
            id="fixed"
            :checked="optionsStore.casesQuickFilterType === CaseQuickFilterType.Fixed"
            type="radio"
            name="quickFilter"
            :value="CaseQuickFilterType.Fixed"
            @change.stop="(e) => optionsStore.setCasesQuickFilterType(parseInt((e.target as HTMLInputElement).value))"
          />
          <label for="fixed">Fixed</label><br />
          <input
            id="slide"
            :checked="optionsStore.casesQuickFilterType === CaseQuickFilterType.Slide"
            type="radio"
            name="quickFilter"
            :value="CaseQuickFilterType.Slide"
            @change.stop="(e) => optionsStore.setCasesQuickFilterType(parseInt((e.target as HTMLInputElement).value))"
          />
          <label for="slide">Slide</label><br />
          <input
            id="none"
            :checked="optionsStore.casesQuickFilterType === CaseQuickFilterType.None"
            type="radio"
            name="quickFilter"
            :value="CaseQuickFilterType.None"
            @change.stop="(e) => optionsStore.setCasesQuickFilterType(parseInt((e.target as HTMLInputElement).value))"
          />
          <label for="none">None</label>
          <p class="option-description">Buttons at the top of the screen to filter by priority.</p>
        </li>
        <li class="list-group-item">
          <span class="icon fa-sm fa-solid fa-list"></span>
          <span class="text ms-2">Show Unacknowledged Quick Filters</span>
          <br />
          <input
            v-model="optionsStore.showUnacknowledgedQuickFilter"
            data-testid="show-unacknowledged-quick-filter"
            type="checkbox"
            @change.stop="() => optionsStore.setUnacknowledgedQuickFilter(optionsStore.showUnacknowledgedQuickFilter)"
          />
          <p class="option-description">Additional Quick Filter to show only unacknowledged cases.</p>
        </li>
        <li class="list-group-item">
          <span class="icon fa-sm fa-solid fa-list"></span>
          <span class="text ms-2">Show Refresh Button in Cases NavBar</span>
          <br />
          <input
            v-model="optionsStore.showRefreshButton"
            data-testid="show-refresh"
            type="checkbox"
            @change.stop="() => optionsStore.setRefreshVisibility(optionsStore.showRefreshButton)"
          />
          <p class="option-description">Button to manually refresh the Case List.</p>
        </li>
        <li class="list-group-item">
          <span class="icon fa-sm fa-solid fa-list"></span>
          <span class="text ms-2">Show Case Detail Map</span>
          <br />
          <input
            v-model="optionsStore.showCaseMap"
            data-testid="show-refresh"
            type="checkbox"
            @change.stop="caseNavBarOptions.onSaveMap"
          />
          <p class="option-description">Show small map of truck location in case detail.</p>
        </li>
        <li class="list-group-item">
          <span class="icon fa-sm fa-solid fa-list"></span>
          <span class="text ms-2">Show Data Icon Labels</span>
          <br />
          <input
            v-model="optionsStore.showDataIconLabels"
            data-testid="show-refresh"
            type="checkbox"
            @change.stop="() => optionsStore.setDataIconLabels(optionsStore.showDataIconLabels)"
          />
          <p class="option-description">Toggle text labels on Data Icons in Case List.</p>
        </li>
        <li class="list-group-item">
          <span class="icon fa-sm fa-solid fa-list"></span>
          <span class="text ms-2">Show Timeline Filters</span>
          <br />
          <input
            v-model="optionsStore.showTimelineFilters"
            data-testid="show-refresh"
            type="checkbox"
            @change.stop="() => optionsStore.setTimelineFilters(optionsStore.showTimelineFilters)"
          />
          <p class="option-description">Quick Filters to show specific items in Case Detail timeline.</p>
        </li>
        <li class="list-group-item">
          <span class="icon fa-sm fa-solid fa-list"></span>
          <span class="text ms-2">Show Timers</span>
          <br />
          <input
            v-model="optionsStore.showTimers"
            data-testid="show-timers"
            type="checkbox"
            @change.stop="() => optionsStore.setTimers(optionsStore.showTimers)"
          />
          <p class="option-description">Toggle Case Detail Timers - EMS Time, ED Time.</p>
        </li>
        <li class="list-group-item">
          <span class="icon fa-sm fa-solid fa-list"></span>
          <span class="text ms-2">Group Cases List By Hospital</span>
          <br />
          <input
            v-model="optionsStore.groupCaseListByHospital"
            data-testid="show-group-by-hospital"
            type="checkbox"
            @change.stop="() => optionsStore.setHospitalGroupBy(optionsStore.groupCaseListByHospital)"
          />
          <p class="option-description">Group Cases List By Hospital instead of Created Time.</p>
        </li>

        <li v-if="casesStore.mode === CaseMode.mih" class="list-group-item">
          <span class="icon fa-list fa-sm fa-solid"></span>

          <span class="text ms-2">Audio Alerts for Updates</span>

          <AudioNotificationForCaseUpdates></AudioNotificationForCaseUpdates>

          <p class="option-description">Play sound when new data is available in a Visit</p>
        </li>
      </ul>
      <br />
      <button class="btn btn-sm btn-outline-primary" @click.stop="optionsStore.restoreDefaults">
        Restore Defaults
      </button>
    </div>
  </div>
</template>
<style scoped>
.no-width {
  width: 0rem;
}

.option-description {
  font-style: italic;
  font-size: 80%;
  margin-top: 0.5rem;
  margin-bottom: -0.25rem;
}

label {
  margin-left: 0.25rem;
}
</style>
